import type { Descendant } from 'slate';
import { Element as SlateElement, Node as SlateNode } from 'slate';
import type { CustomElement } from '../../../components/editor/slate-types';
import { htmlToSlate, slateToHtml } from '../../../components/editor/slate-html';
import { htmlHasSlateRichFormatting } from '../composeBody/classifyBodyKind';
import { REPLY_FORWARD_METADATA_HEADER_STYLE } from './replyForwardHeaderStyle';
import { sanitizeRichEmailHtml } from './sanitizeEmailBody';
import { normalizePlainText, isPlainTextOpsTemplate } from '../composeBody/normalizePlainText';
import { effectivePlainTextBody } from './phoenixPlaceholderPlainText';

/** Must match Compose.tsx (late signature insertion if raw-html was already unlocked). */
const REPLY_FORWARD_HEADER_ANCHOR_COMMENT = 'ums-rf-h';

function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function escapeHtmlPlain(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function approxTextLength(html: string): number {
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || '').length;
}

/**
 * Reply header uses `# <strong>From:</strong> … <br />` but htmlToSlate only walks element
 * children, so mixed text + strong + br never became blocks. Split on `<br>` into `<p>` rows.
 */
function replyHeaderInnerToParagraphHtml(innerHtml: string): string {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = innerHtml.trim();
  const chunks: string[] = [];
  const buf = document.createElement('div');
  const flush = () => {
    const h = buf.innerHTML.trim();
    if (h) chunks.push(h);
    buf.innerHTML = '';
  };
  for (const node of Array.from(wrapper.childNodes)) {
    if (node.nodeType === globalThis.Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br') {
      flush();
    } else {
      buf.appendChild(node.cloneNode(true));
    }
  }
  flush();
  if (chunks.length === 0) chunks.push('<br />');
  return chunks.map((c) => `<p>${c}</p>`).join('');
}

function decodedToEditableHtml(decoded: string): string {
  const origLen = approxTextLength(decoded);
  const wrapped = `<div>${decoded}</div>`;
  const nodes = htmlToSlate(wrapped);
  let out = slateToHtml(nodes);
  const outLen = approxTextLength(out);

  if (origLen >= 25 && outLen < origLen * 0.25) {
    return '';
  }

  const stripped = out.replace(/<[^>]+>/g, '').replace(/\s/g, '');
  const decodedStripped = decoded.replace(/<[^>]+>/g, '').replace(/\s/g, '');
  if ((!stripped || stripped.length < Math.min(3, decodedStripped.length)) && decodedStripped.length > 0) {
    const tmp = document.createElement('div');
    tmp.innerHTML = decoded;
    const text = tmp.textContent ?? '';
    const lines = text.split(/\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) {
      out = lines.map((p) => `<div>${escapeHtmlPlain(p)}</div>`).join('');
    } else if (text.trim()) {
      const fallback: CustomElement = { type: 'body-text', children: [{ text: text.trim().length > 0 ? text.trim() : ' ' }] };
      out = slateToHtml([fallback]);
    }
  }
  return out;
}

const PRE_INLINE_STYLE =
  'margin:0;padding:0;white-space:pre;overflow-x:auto;font-family:ui-monospace,\'Courier New\', Courier, monospace;font-size:13px;line-height:1.2;tab-size:8;-moz-tab-size:8';

function plainTextToPreHtml(text: string): string {
  return `<pre class="email-template-pre" style="${PRE_INLINE_STYLE}">${escapeHtmlPlain(normalizePlainText(text))}</pre>`;
}

/** Plain text → Slate Body Text blocks (`<div>`), matching Thunderbird default for imports. */
export function plainTextToEditableComposeHtml(text: string): string {
  const normalized = normalizePlainText(text);
  if (!normalized.trim()) return '<div><br></div>';
  return normalized
    .split('\n')
    .map((line) => {
      if (!line.trim()) return '<div><br></div>';
      return `<div>${escapeHtmlPlain(line.trimEnd())}</div>`;
    })
    .join('');
}

/** Empty editable placeholder (`<p><br></p>` or Body Text `<div><br></div>`). */
function isEmptyEditableComposePlaceholder(html: string): boolean {
  const t = html.trim();
  if (!t) return true;
  return /^<(p|div)>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>$/i.test(t);
}

/**
 * Prefer Thunderbird Body Text (`<div>`) over Paragraph (`<p>`) for imported library /
 * compose content. Leaves paragraphs inside lists, tables, and quotes alone.
 */
export function convertParagraphsToBodyText(html: string): string {
  if (!html?.trim()) return html;
  if (typeof document === 'undefined') {
    return html.replace(/<p(\s[^>]*)?>/gi, '<div$1>').replace(/<\/p>/gi, '</div>');
  }
  const doc = new DOMParser().parseFromString(`<div id="bt-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('bt-root');
  if (!root) return html;
  for (const p of Array.from(root.querySelectorAll('p'))) {
    if (p.closest('li, td, th, blockquote, pre')) continue;
    const div = doc.createElement('div');
    for (const attr of Array.from(p.attributes)) {
      div.setAttribute(attr.name, attr.value);
    }
    while (p.firstChild) div.appendChild(p.firstChild);
    p.replaceWith(div);
  }
  return root.innerHTML;
}

/** Unwrap legacy quoted-rich blocks saved in compose userBody into editable paragraphs. */
export function unwrapQuotedRichComposeHtml(html: string): string {
  if (!html.includes('data-slate-quoted-rich')) return html;
  if (typeof document === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div id="unwrap-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('unwrap-root');
  if (!root) return html;

  const quoted = root.querySelector('[data-slate-quoted-rich="true"]');
  if (!quoted) return html;

  const encoded = quoted.getAttribute('data-html');
  if (!encoded) return html;

  try {
    const decoded = decodeURIComponent(encoded);
    if (isPlainPreTemplateHtml(decoded)) {
      return plainTextToEditableComposeHtml(preTemplateHtmlToPlainText(decoded));
    }
    const rich = richTemplateHtmlToEditableEditorHtml(decoded);
    if (rich.trim() && !isEmptyEditableComposePlaceholder(rich)) return convertParagraphsToBodyText(rich);
    const editable = decodedToEditableHtml(decoded);
    if (editable.trim()) return convertParagraphsToBodyText(editable);
    const fallbackText = preTemplateHtmlToPlainText(decoded) || decoded.replace(/<[^>]+>/g, '');
    return plainTextToEditableComposeHtml(fallbackText);
  } catch {
    return html;
  }
}

/** Normalize newlines, trim trailing spaces per line, reduce double-spaced .eml text. */
function normalizePlainTextForEditor(text: string): string {
  return normalizePlainText(text);
}

function shouldPreferPlainTextBody(
  bodyHtml: string | null | undefined,
  bodyText: string | null | undefined,
  bodyKind?: 'plain' | 'rich' | null,
): boolean {
  const effectiveText = effectivePlainTextBody(bodyText);
  // Maritime ops / Phoenix FONT+BR letters: always prefer line-based Body Text.
  if (effectiveText && isPlainTextOpsTemplate(effectiveText)) return true;
  if (bodyKind === 'rich') return false;
  if (effectiveText == null || effectiveText.length === 0) return false;
  const htmlStr = bodyHtml?.trim() ?? '';
  if (!htmlStr || isTrivialHtml(htmlStr)) return true;
  if (htmlLooksLikePlainTextReport(htmlStr)) return true;
  if (htmlHasSlateRichFormatting(htmlStr)) return false;
  if (!effectiveText.includes('\n')) return false;
  if (!htmlHasRichContent(htmlStr)) return true;
  if (plainTextMatchesHtmlContent(effectiveText, htmlStr)) return true;
  if (/mso-|word\.document|<o:p/i.test(htmlStr)) return true;
  return false;
}

export function isPlainPreTemplateHtml(html: string): boolean {
  return /^<pre\b[\s\S]*<\/pre>\s*$/i.test(html.trim());
}

export function preTemplateHtmlToPlainText(html: string): string {
  if (typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('pre')?.textContent ?? '';
}

export function wrapPlainTextAsPreHtml(text: string): string {
  return plainTextToPreHtml(text);
}

function blockContainsInlineImage(block: Descendant): boolean {
  if (!SlateElement.isElement(block)) return false;
  const el = block as CustomElement;
  if (el.type === 'image') return true;
  for (const child of el.children ?? []) {
    if (SlateElement.isElement(child) && (child as CustomElement).type === 'image') return true;
  }
  return false;
}

/** When Outlook HTML was parsed into many Slate paragraphs, merge into one monospace pre block. */
export function coalescePlainTextParagraphBlocks(blocks: Descendant[]): Descendant[] | null {
  if (blocks.length < 4) return null;
  // Signatures/logos: never merge away inline images (image paragraphs have empty SlateNode.string).
  if (blocks.some(blockContainsInlineImage)) return null;
  const paras = blocks.filter(
    (b): b is CustomElement =>
      SlateElement.isElement(b) &&
      ((b as CustomElement).type === 'paragraph' || (b as CustomElement).type === 'body-text'),
  );
  if (paras.length < 4 || paras.length / blocks.length < 0.85) return null;
  const lines = paras.map((p) => SlateNode.string(p).replace(/\u00a0/g, ' '));
  const joined = lines.join('\n');
  if (!isPlainTextOpsTemplate(joined.replace(/\n+/g, '\n')) && paras.length < 8) return null;
  const short = lines.filter((l) => l.trim().length <= 120).length;
  if (short / lines.length < 0.8) return null;
  return [
    {
      type: 'quoted-rich-html',
      html: plainTextToPreHtml(joined),
      children: [{ text: '' }],
    } as CustomElement,
  ];
}

function isTrivialHtml(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length === 0;
}

/** Plain text and HTML body carry the same words (typical multipart .eml/.msg). */
function plainTextMatchesHtmlContent(text: string, html: string): boolean {
  if (typeof document === 'undefined') return false;
  const doc = new DOMParser().parseFromString(`<div id="cmp">${html}</div>`, 'text/html');
  const root = doc.getElementById('cmp');
  const htmlText = (root?.textContent ?? '').replace(/\s+/g, ' ').trim();
  const plain = text.replace(/\s+/g, ' ').trim();
  if (!plain || !htmlText) return false;
  const ratio = plain.length / htmlText.length;
  return ratio > 0.82 && ratio < 1.18;
}

/** Rebuild plain-text layout from HTML that uses one block element per line. */
function extractTextWithNewlines(root: Element): string {
  const parts: string[] = [];
  const blockTags = new Set(['p', 'div', 'pre', 'tr', 'li', 'h1', 'h2', 'h3', 'hr', 'blockquote']);

  const walk = (node: globalThis.Node) => {
    if (node.nodeType === globalThis.Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== globalThis.Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'br') {
      parts.push('\n');
      return;
    }
    if (tag === 'pre') {
      parts.push(el.textContent ?? '');
      return;
    }
    for (const child of Array.from(el.childNodes)) {
      walk(child);
    }
    if (blockTags.has(tag)) {
      parts.push('\n');
    }
  };

  for (const child of Array.from(root.childNodes)) {
    walk(child);
  }
  return normalizePlainTextForEditor(parts.join('').replace(/\r\n/g, '\n'));
}

function htmlLooksLikePlainTextReport(html: string): boolean {
  if (typeof document === 'undefined') return false;
  if (/<pre\b/i.test(html)) return false;
  const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, 'text/html');
  const root = doc.getElementById('r');
  if (!root) return false;
  const text = extractTextWithNewlines(root);
  // Phoenix PH2004 uses FONT/SPAN styles but content is a plain FM:/TO: report.
  if (isPlainTextOpsTemplate(text)) return true;
  if (htmlHasSlateRichFormatting(html)) return false;
  const ps = root.querySelectorAll('p');
  if (ps.length >= 2) {
    const short = Array.from(ps).filter((p) => (p.textContent?.length ?? 0) <= 120).length;
    return short / ps.length >= 0.8;
  }
  const divs = root.querySelectorAll('div');
  if (divs.length >= 4 && ps.length === 0) {
    const short = Array.from(divs).filter((d) => (d.textContent?.length ?? 0) <= 120).length;
    return short / divs.length >= 0.85;
  }
  return false;
}

function htmlHasRichContent(html: string): boolean {
  if (/<img\b/i.test(html)) return true;
  if (htmlHasSlateRichFormatting(html)) return true;
  if (!/<table\b/i.test(html)) return false;
  if (typeof document === 'undefined') return false;
  const doc = new DOMParser().parseFromString(`<div id="rich">${html}</div>`, 'text/html');
  const root = doc.getElementById('rich');
  if (!root) return false;
  for (const table of Array.from(root.querySelectorAll('table'))) {
    const rows = table.querySelectorAll('tr').length;
    const cells = table.querySelectorAll('td, th').length;
    if (rows <= 1 && cells <= 1) continue;
    if (rows === 1 && cells <= 4) continue;
    if (rows > 2 || cells > 8) return true;
  }
  return false;
}

function htmlShouldBecomePre(html: string): boolean {
  if (/<pre\b/i.test(html)) return false;
  if (htmlHasRichContent(html)) return false;
  return htmlLooksLikePlainTextReport(html);
}

function normalizePlainTemplateHtml(html: string): string {
  if (typeof document === 'undefined') return html;
  if (/<pre\b/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(`<div id="tpl-plain">${html}</div>`, 'text/html');
  const root = doc.getElementById('tpl-plain');
  if (!root) return html;
  const text = normalizePlainTextForEditor(extractTextWithNewlines(root));
  return plainTextToPreHtml(text);
}

function templateBodyToQuotedRichBlock(decoded: string): string {
  let normalized = decoded;
  if (!/<pre\b/i.test(decoded) && htmlShouldBecomePre(decoded)) {
    normalized = normalizePlainTemplateHtml(decoded);
  }
  const safe = /<pre\b/i.test(normalized)
    ? sanitizeRichEmailHtml(normalized)
    : collapseRedundantTemplateSpacing(sanitizeRichEmailHtml(normalized));
  if (safe.trim()) {
    return `<div data-slate-quoted-rich="true" data-html="${encodeURIComponent(safe)}"></div>`;
  }
  const plain = decodedToEditableHtml(decoded);
  return plain.trim() || '<div><br></div>';
}

/** Remove Outlook-style empty paragraphs and inline spacing that adds gaps in the editor. */
function collapseRedundantTemplateSpacing(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div id="tpl-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('tpl-root');
  if (!root) return html;

  root.querySelectorAll('o\\:p, o\\:P, span, font').forEach((el) => {
    const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    const hasMedia = el.querySelector('img, table, svg, hr, pre');
    if (!text && !hasMedia && el.tagName.toLowerCase() !== 'font') el.remove();
  });

  let removed = true;
  while (removed) {
    removed = false;
    root.querySelectorAll('br').forEach((br) => {
      let next: ChildNode | null = br.nextSibling;
      while (next && next.nodeType === globalThis.Node.TEXT_NODE && !(next.textContent ?? '').trim()) {
        next = next.nextSibling;
      }
      if (next?.nodeName?.toLowerCase() === 'br') {
        br.remove();
        removed = true;
      }
    });
  }

  root.querySelectorAll('p, div').forEach((el) => {
    const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    const hasMedia = el.querySelector('img, table, svg, hr, pre');
    const onlyBr = !text && el.querySelector('br') && !hasMedia;
    if ((!text && !hasMedia) || onlyBr) el.remove();
  });

  root.querySelectorAll('p, div, span, li, td, tr, font').forEach((el) => {
    const style = el.getAttribute('style');
    if (!style) return;
    const cleaned = style
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => {
        const prop = part.split(':')[0]?.trim().toLowerCase() ?? '';
        return !/^(margin|padding|line-height|min-height|height|mso-|font-size)/.test(prop);
      })
      .join('; ');
    if (cleaned.trim()) el.setAttribute('style', cleaned);
    else el.removeAttribute('style');
  });

  return root.innerHTML;
}

/** True when HTML was saved from Compose (Slate), not imported from EML/MSG/Word. */
function isComposeSavedTemplateBody(html: string): boolean {
  const lower = html.toLowerCase();
  if (lower.includes('<table') || lower.includes('mso-') || lower.includes('<o:p')) return false;
  if (lower.includes('data-slate-raw-html') || lower.includes('data-slate-quoted-rich')) return false;
  if (html.includes('email-signature')) return true;
  const withoutSig = html.replace(/<div class="email-signature"[\s\S]*?<\/div>/gi, '');
  if (/<(?:table|tbody|thead|tr|td|th|font)\b/i.test(withoutSig)) return false;
  // Paragraph or Body Text blocks from Slate (with optional styled spans).
  if (/<(?:p|div|h[1-6]|address|pre)\b/i.test(withoutSig)) {
    // Reject wrapper-div layouts that nest many styled block containers (imported HTML).
    if (/<div[\s>][^>]*style=/i.test(withoutSig) && !html.includes('<p') && !/<div>\s*<span\b/i.test(withoutSig)) {
      return false;
    }
    return true;
  }
  return false;
}

/** Expand quoted-rich / raw-html wrappers for preview or print (shows inner HTML). */
export function expandComposeEditorHtmlForDisplay(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div id="expand-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('expand-root');
  if (!root) return html;

  root.querySelectorAll('div[data-slate-quoted-rich="true"], div[data-slate-raw-html="true"]').forEach((el) => {
    const encoded = el.getAttribute('data-html');
    if (!encoded) return;
    try {
      const decoded = decodeURIComponent(encoded);
      const container = doc.createElement('div');
      container.innerHTML = decoded;
      const parent = el.parentNode;
      if (!parent) return;
      while (container.firstChild) {
        parent.insertBefore(container.firstChild, el);
      }
      parent.removeChild(el);
    } catch {
      // keep wrapper if decode fails
    }
  });

  return root.innerHTML;
}

/** Replace locked raw-html iframe blocks with editable Slate / quoted-rich blocks. */
export function unlockTemplateBodyForEditing(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div id="unlock-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('unlock-root');
  if (!root) return html;

  const convertRawHtml = (el: Element) => {
    const encoded = el.getAttribute('data-html');
    if (!encoded) return;
    let decoded = '';
    try {
      decoded = decodeURIComponent(encoded);
    } catch {
      return;
    }
    if (!decoded.trim()) return;

    let outHtml: string;
    if (el.hasAttribute('data-slate-inline-html')) {
      const rowHtml = replyHeaderInnerToParagraphHtml(decoded);
      outHtml = decodedToEditableHtml(rowHtml) || rowHtml;
    } else {
      outHtml = templateBodyToQuotedRichBlock(decoded);
    }
    if (!outHtml.trim()) return;

    const container = doc.createElement('div');
    container.innerHTML = outHtml;
    const parent = el.parentNode;
    if (!parent) return;
    while (container.firstChild) {
      parent.insertBefore(container.firstChild, el);
    }
    parent.removeChild(el);
  };

  root.querySelectorAll('div[data-slate-raw-html="true"]').forEach(convertRawHtml);
  return root.innerHTML;
}

/** Strip outer &lt;html&gt;/&lt;head&gt; wrapper from saved or imported message HTML. */
export function extractHtmlBodyFragment(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;
  if (typeof document === 'undefined') return trimmed;
  const probe = trimmed.slice(0, 300).toLowerCase();
  const looksLikeDocument =
    /^<!doctype\s+html/i.test(trimmed) ||
    probe.startsWith('<html') ||
    (/<head[\s>]/i.test(probe) && /<body[\s>]/i.test(trimmed));
  if (!looksLikeDocument) return trimmed;
  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const bodyHtml = doc.body?.innerHTML?.trim();
  return bodyHtml || trimmed;
}

function htmlBodyToEditableParagraphs(html: string): string {
  const fragment = extractHtmlBodyFragment(html);
  if (isPlainPreTemplateHtml(fragment)) {
    return plainTextToEditableComposeHtml(preTemplateHtmlToPlainText(fragment));
  }
  const editable = decodedToEditableHtml(fragment);
  if (editable.trim() && !isEmptyEditableComposePlaceholder(editable)) return convertParagraphsToBodyText(editable);
  if (typeof document === 'undefined') return fragment;
  const doc = new DOMParser().parseFromString(fragment, 'text/html');
  const root = doc.body ?? doc.documentElement;
  const text = extractTextWithNewlines(root);
  if (text.trim()) return plainTextToEditableComposeHtml(text);
  return editable.trim() || '<div><br></div>';
}

/** True when compose userBody is locked import HTML that should be re-converted for editing. */
export function userBodyNeedsImportUnlock(html: string | null | undefined): boolean {
  const h = html?.trim() ?? '';
  if (!h) return false;
  if (h.includes('data-slate-quoted-rich') || h.includes('data-slate-raw-html')) return true;
  if (isPlainPreTemplateHtml(h)) return true;
  const probe = h.slice(0, 300).toLowerCase();
  if (probe.startsWith('<!doctype') || probe.startsWith('<html') || (/<head[\s>]/i.test(probe) && /<body[\s>]/i.test(h))) {
    return true;
  }
  if (/mso-|word\.document|<o:p/i.test(h) && !htmlHasSlateRichFormatting(h)) return true;
  if (h.length > 1500 && !htmlHasSlateRichFormatting(h) && !isComposeSavedTemplateBody(h)) return true;
  return false;
}

function finalizeImportedEditorHtml(html: string, bodyText?: string | null): string {
  let result = unwrapQuotedRichComposeHtml(html);
  if (result.includes('data-slate-quoted-rich') || result.includes('data-slate-raw-html')) {
    result = unwrapQuotedRichComposeHtml(expandComposeEditorHtmlForDisplay(unlockTemplateBodyForEditing(result)));
  }
  const text = effectivePlainTextBody(bodyText);
  if (text && userBodyNeedsImportUnlock(result)) {
    return plainTextToEditableComposeHtml(text);
  }
  const trimmed = result.trim() || '<div><br></div>';
  return convertParagraphsToBodyText(trimmed);
}

function richTemplateHtmlToEditableEditorHtml(raw: string): string {
  if (raw.includes('data-slate-quoted-rich')) return unwrapQuotedRichComposeHtml(raw);
  if (raw.includes('data-slate-raw-html')) return unlockTemplateBodyForEditing(raw);
  if (isComposeSavedTemplateBody(raw)) return convertParagraphsToBodyText(raw);
  return htmlBodyToEditableParagraphs(raw);
}

/**
 * Map a library template body to editable Compose HTML (never locked raw-html iframes).
 */
export function templateBodyToEditableEditorHtml(
  bodyHtml?: string | null,
  bodyText?: string | null,
  options?: { bodyKind?: 'plain' | 'rich' | null },
): string {
  const bodyKind = options?.bodyKind;
  const effectiveText = effectivePlainTextBody(bodyText);
  const raw = bodyHtml?.trim() ?? '';

  let out = '';
  if (bodyKind === 'rich' && raw) {
    out = richTemplateHtmlToEditableEditorHtml(raw);
  } else if (shouldPreferPlainTextBody(bodyHtml, effectiveText, bodyKind)) {
    out = plainTextToEditableComposeHtml(effectiveText!);
  } else if (!raw && effectiveText != null && effectiveText.length > 0) {
    out = plainTextToEditableComposeHtml(effectiveText);
  } else if (!raw) {
    return '';
  } else if (typeof document === 'undefined') {
    return convertParagraphsToBodyText(raw);
  } else if (!/<(?:p|div|table|pre|br|span|font|strong|b|em|i)\b/i.test(raw)) {
    // Plain text stored in bodyHtml (no markup) — one Body Text block per line.
    out = plainTextToEditableComposeHtml(raw);
  } else if (raw.includes('data-slate-quoted-rich')) {
    out = unwrapQuotedRichComposeHtml(raw);
  } else if (raw.includes('data-slate-raw-html')) {
    out = unlockTemplateBodyForEditing(raw);
  } else if (htmlHasSlateRichFormatting(raw)) {
    out = richTemplateHtmlToEditableEditorHtml(raw);
  } else if (isComposeSavedTemplateBody(raw)) {
    out = raw;
  } else if (htmlLooksLikePlainTextReport(raw)) {
    if (typeof document !== 'undefined') {
      const doc = new DOMParser().parseFromString(`<div id="r">${raw}</div>`, 'text/html');
      const root = doc.getElementById('r');
      if (root) out = plainTextToEditableComposeHtml(extractTextWithNewlines(root));
    }
    if (!out) out = plainTextToEditableComposeHtml(raw);
  } else {
    const rich = richTemplateHtmlToEditableEditorHtml(raw);
    if (rich.trim() && !isEmptyEditableComposePlaceholder(rich)) {
      out = rich;
    } else if (typeof document !== 'undefined') {
      const doc = new DOMParser().parseFromString(`<div id="fallback">${raw}</div>`, 'text/html');
      const root = doc.getElementById('fallback');
      if (root) out = plainTextToEditableComposeHtml(extractTextWithNewlines(root));
    }
    if (!out) out = raw;
  }

  return convertParagraphsToBodyText(out);
}

/**
 * Map an imported .eml/.msg body to editable Compose HTML.
 * Prefers the plain-text part when HTML is an Outlook/Word conversion of the same content.
 */
export function composeImportedMessageToEditableEditorHtml(
  bodyHtml?: string | null,
  bodyText?: string | null,
  options?: { bodyKind?: 'plain' | 'rich' | null },
): string {
  void options;
  return importMessageToEditableEditorHtml(bodyHtml, bodyText);
}

/**
 * Map an imported .eml/.msg body to editable Compose HTML (rich Slate editor).
 * Prefers direct editable HTML over locked quoted blocks when possible.
 * Uses Body Text (`<div>`) as the default block style.
 */
export function importMessageToEditableEditorHtml(
  bodyHtml?: string | null,
  bodyText?: string | null,
): string {
  const raw = bodyHtml?.trim() ?? '';
  const text = effectivePlainTextBody(bodyText);

  if (shouldPreferPlainTextBody(bodyHtml, bodyText, null) && text) {
    return plainTextToEditableComposeHtml(text);
  }

  // Phoenix / ops HTML with no usable text part: still map to BodyText lines.
  if (raw && !isTrivialHtml(raw) && htmlLooksLikePlainTextReport(raw)) {
    if (typeof document !== 'undefined') {
      const doc = new DOMParser().parseFromString(`<div id="ops">${raw}</div>`, 'text/html');
      const root = doc.getElementById('ops');
      if (root) {
        const extracted = extractTextWithNewlines(root);
        if (extracted.trim()) {
          return finalizeImportedEditorHtml(plainTextToEditableComposeHtml(extracted), bodyText);
        }
      }
    }
  }

  let result = '';
  if (raw && !isTrivialHtml(raw)) {
    result = richTemplateHtmlToEditableEditorHtml(raw);
  }
  if (!result.trim() || isEmptyEditableComposePlaceholder(result)) {
    const heuristic = templateBodyToEditableEditorHtml(bodyHtml, bodyText);
    if (heuristic.trim() && !isEmptyEditableComposePlaceholder(heuristic)) result = heuristic;
  }
  if (!result.trim() || isEmptyEditableComposePlaceholder(result)) {
    if (text) result = plainTextToEditableComposeHtml(text);
    else result = raw;
  }

  return finalizeImportedEditorHtml(result, bodyText);
}

/**
 * Turns reply/forward locked regions (raw-html / iframe) into normal HTML blocks
 * so the Slate editor can edit them. Does not change content above the signature.
 */
export function unlockReplyForwardBelowSignature(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div id="unlock-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('unlock-root');
  if (!root) return html;

  const convertDiv = (el: Element) => {
    const encoded = el.getAttribute('data-html');
    if (!encoded) return;
    let decoded = '';
    try {
      decoded = decodeURIComponent(encoded);
    } catch {
      return;
    }
    if (!decoded.trim()) return;
    const isInlineHeader = el.hasAttribute('data-slate-inline-html');
    if (isInlineHeader) {
      const prev = el.previousSibling;
      const hasAnchor =
        prev?.nodeType === globalThis.Node.COMMENT_NODE &&
        (prev as Comment).data.trim() === REPLY_FORWARD_HEADER_ANCHOR_COMMENT;
      if (!hasAnchor && el.parentNode) {
        el.parentNode.insertBefore(doc.createComment(REPLY_FORWARD_HEADER_ANCHOR_COMMENT), el);
      }
    }
    let outHtml: string;
    if (isInlineHeader) {
      const wrap = doc.createElement('div');
      wrap.innerHTML = decoded.trim();
      const first = wrap.firstElementChild;
      let innerSlate: string;
      let boxStyle: string;
      if (first && first.tagName.toLowerCase() === 'div') {
        boxStyle = first.getAttribute('style') || REPLY_FORWARD_METADATA_HEADER_STYLE;
        const rowHtml = replyHeaderInnerToParagraphHtml(first.innerHTML);
        innerSlate = decodedToEditableHtml(rowHtml);
        if (!innerSlate.trim()) {
          innerSlate = rowHtml;
        }
      } else {
        boxStyle = REPLY_FORWARD_METADATA_HEADER_STYLE;
        innerSlate = decodedToEditableHtml(decoded);
        if (!innerSlate.trim()) {
          innerSlate = replyHeaderInnerToParagraphHtml(decoded);
        }
      }
      outHtml = `<div data-ums-reply-header="true" style="${escapeHtmlAttr(boxStyle)}">${innerSlate}</div>`;
    } else {
      const safe = sanitizeRichEmailHtml(decoded);
      if (safe.trim()) {
        outHtml = `<div data-slate-quoted-rich="true" data-html="${encodeURIComponent(safe)}"></div>`;
      } else {
        outHtml = decodedToEditableHtml(decoded);
      }
    }
    if (!outHtml.trim()) return;
    const container = doc.createElement('div');
    container.innerHTML = outHtml;
    const parent = el.parentNode;
    if (!parent) return;
    while (container.firstChild) {
      parent.insertBefore(container.firstChild, el);
    }
    parent.removeChild(el);
  };

  root.querySelectorAll('blockquote.email-quoted-block div[data-slate-raw-html="true"]').forEach((el) => convertDiv(el));

  root.querySelectorAll('div[data-slate-inline-html="true"][data-slate-raw-html="true"]').forEach((el) => convertDiv(el));

  return root.innerHTML;
}
